<template>
  <div
    class="impact-slider"
    :class="elClasses"
  >
    <div class="v-centerer impact-slider__wrapper">
      <div v-if="legend !== null && legend !== ''" class="legend impact-slider__legend">
        {{ legend }}
      </div>

      <VueSlider
        ref="slider"
        :value="value"
        v-bind="sliderProps"
        class="impact-slider__slider"
        @change="$emit('input', $event)"
      >

        <template v-slot:step="{ label, active }">
          <div class="slider-mark">
            <div class="slider-mark-joint" />
          </div>
        </template>

        <template v-slot:label="{ value }">
          <div
            v-if="hintPerValue[`${value}`] !== undefined"
            class="slider-hint field-hint"
            :style="hintPerValue[`${value}`].style"
          >
            <span v-quick-title="hintPerValue[`${value}`].text" :style="hintPerValue[`${value}`].spanStyle">
              {{ hintPerValue[`${value}`].text }}
            </span>
          </div>
        </template>

      </VueSlider>

    </div>

    <div v-if="hints.length > 0" class="impact-slider__footer">
      <div class="impact-slider__footer__blue-line" />
      <Checkbox
        v-model="displayHints"
        :title="$t('message.DISPLAY_FULL_IMPACT_HINTS')"
      />
      <div v-if="displayHints" class="field-hint impact-slider__footer__full-hints">
        <div v-for="hint in sortedHints">
          <span class="impact-slider__footer__full-hints__hint-val">
            {{ `${hint.impact}${$t('message.TWO_DOTS')}` }}
          </span>
          <span class="impact-slider__footer__full-hints__hint-text">
            {{ hint.text }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Impact slider with custom hints
 * Display an editable VueSlider component.
 *
 * @vue-prop {Number} value                - Value of the slider, edited on 'input'
 * @vue-prop {Array} hint                  - Hints to display below the slider for each value
 * @vue-prop {String} [legend='']          - Legend to display aligned with the slider
 * @vue-prop {Boolean} [showTooltip=false] - If true, display the slider tooltip when editing the value
 *
 * @vue-event {Number} input          - Emit the new value on edition
 */

import { cloneDeep } from 'lodash';
// Components
import Checkbox from '@/components/checkbox.vue';
import VueSlider from 'vue-slider-component';
// Utils
import { sliderProperties } from '@/utils/main';

export default {

  name: 'ImpactSlider',

  components: {
    Checkbox,
    VueSlider,
  },

  props: {
    hints: {
      type: Array,
      required: true,
    },
    value: {
      type: Number,
      required: true,
    },
    legend: {
      type: String,
      default: null,
    },
    showTooltip: Boolean,
  },

  data() {
    return {
      elWidth: null,
      displayHints: false,
    };
  },

  computed: {
    elClasses() {
      return {
        'impact-slider--with-hints': this.hints.length > 0,
        'impact-slider--has-legend': this.legend !== null && this.legend !== '',
      };
    },
    /** Object: Properties of the impact slider */
    sliderProps() {
      const props = Object.assign({}, sliderProperties);
      delete props.width;
      delete props.padding;
      if (!this.showTooltip) {
        props.tooltipStyle = { display: 'none' };
      }
      props.marks = val => this.sliderStepValues.includes(val);
      return props;
    },
    /** Array: props.hints sorted in increasing order */
    sortedHints() {
      return this.hints.sort((h1, h2) => h1.impact - h2.impact);
    },
    /** Object: <slider_value>: <hint_text> */
    hintPerValue() {
      if (!this.elWidth) {
        return {};
      }
      const cHints = cloneDeep(this.sortedHints);
      const margin = this.$store.getters.fontSize * 0.4;
      const leftRightSpace = 8;
      cHints.forEach((hint, i) => {
        // Find left and right space for each hint
        const leftSpace = i === 0
          ? (hint.impact) * this.elWidth / 3 + leftRightSpace
          : (hint.impact - cHints[i - 1].impact) * this.elWidth / 6 - margin;
        const rightSpace = i === cHints.length - 1
          ? (3 - hint.impact) * this.elWidth / 3 + leftRightSpace
          : (cHints[i + 1].impact - hint.impact) * this.elWidth / 6 - margin;

        hint.style = {
          left: `-${leftSpace}px`,
          right: `${rightSpace}px`,
          width: `${leftSpace + rightSpace}px`,
        };
        hint.spanStyle = {
          left: `${leftSpace}px`,
          transform: `translateX(-${leftSpace * 100 / (rightSpace + leftSpace)}%)`,
        };
        if (this.displayHints) {
          hint.text = hint.impact;
        }
      });

      const perVal = cHints.reduce((obj, hint) => ({ ...obj, [`${hint.impact}`]: hint }), {});
      return perVal;
    },

    /** Array: Array of all values at which a hint is defined */
    sliderStepValues() {
      return this.hints.map(hint => hint.impact);
    },
  },

  created() {
    $(window).on(this.resizeEventName, () => {
      this.elWidth = this.$refs.slider.$el.offsetWidth;
    });
  },

  mounted() {
    this.elWidth = this.$refs.slider.$el.offsetWidth;
  },

};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.impact-slider {
  width: 100%;
  white-space: normal;
  text-align: left;

  $label-top-maargin: 0.5em;

  &__legend {
    flex: 0 0 auto;
    margin-right: 0.2em;
  }

  &__wrapper {

  }
  &--with-hints {
    .impact-slider__wrapper {
      margin-bottom: 1.2em;
    }
  }

  &__slider {
    flex: 1;

    .slider-mark {
      height: 100%;
      background: $color-cyan;

      &-joint {
        width: 1px;
        background: $color-cyan;
        position: absolute;
        bottom: -$label-top-maargin;
        top: 0px;
        left: 50%;
        transform: translateX(-50%);
      }
    }
    .slider-hint {
      position: absolute;
      margin-top: $label-top-maargin;
      height: 1.4em;

      span {
        position: absolute;
        display: inline-block;
        border-top: 1px solid $color-cyan;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
    }
  }

  &__footer {
    position: relative;
    padding-left: 0.5em;
    padding-top: 0.2em;
    padding-bottom: 0.3em;

    .checkbox {
      padding: 0;
      font-size: 0.85em;
      font-style: italic;

      .tick {
        margin-right: 0.5em;
      }
    }
    &__blue-line {
      position: absolute;
      top: -0.1em;
      left: 0px;
      bottom: 0px;
      width: 10px;
      border-left: 1px solid $color-cyan;
      border-bottom: 1px solid $color-cyan;
      background: transparent;
    }

    &__full-hints {
      &__hint-val {
        font-weight: 600;
        color: $color-cyan;
      }
    }
  }

  &--has-legend {
    .impact-slider__footer {
      &__blue-line {
        top: -1em;
      }
    }
  }


}
</style>
